import { useEffect, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const NAVY = "#132a63";
const GREEN = "#16a34a";

// Formulario reutilizable de datos de pago + fiscales + documentos.
// Props: getUrl, putUrl, docUrl, isProvider (muestra nombre comercial/fiscal)
export default function PayoutForm({ getUrl, putUrl, docUrl, isProvider = false }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    apiClient.get(getUrl).then(({ data }) => setF(data)).catch(() => toast.error("No se pudo cargar tu información"));
  }, [getUrl]);

  if (!f) return <div style={{ color: "#6b7280", padding: 20 }}>Cargando…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        titular: f.titular, banco: f.banco, tipo_cuenta: f.tipo_cuenta || "clabe",
        cuenta: f.cuenta, rfc: f.rfc, direccion_fiscal: f.direccion_fiscal,
      };
      if (isProvider) { body.nombre_comercial = f.nombre_comercial; body.nombre_fiscal = f.nombre_fiscal; }
      const { data } = await apiClient.put(putUrl, body);
      setF(data);
      toast.success("Datos guardados ✅");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const upload = async (tipo, file) => {
    if (!file) return;
    setUploading(tipo);
    try {
      const fd = new FormData();
      fd.append("tipo", tipo);
      fd.append("file", file);
      await apiClient.post(docUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await apiClient.get(getUrl);
      setF(data);
      toast.success("Documento subido 📎");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setUploading(""); }
  };

  const docs = f.documentos || {};
  return (
    <div data-testid="payout-form">
      {f.completo
        ? <div style={badge(GREEN, "#ecfdf5")} data-testid="payout-status-complete"><i className="fas fa-circle-check" /> Datos completos — listos para recibir depósitos</div>
        : <div style={badge("#b45309", "#fffbeb")} data-testid="payout-status-incomplete"><i className="fas fa-triangle-exclamation" /> Faltan datos: para poder dispersarte el dinero necesitas cuenta, RFC, dirección fiscal y tus 2 documentos.</div>}

      {isProvider && (
        <>
          <div style={grid2}>
            <Field label="Nombre comercial"><input style={inp} value={f.nombre_comercial || ""} onChange={set("nombre_comercial")} data-testid="pf-nombre-comercial" /></Field>
            <Field label="Nombre / Razón social fiscal"><input style={inp} value={f.nombre_fiscal || ""} onChange={set("nombre_fiscal")} data-testid="pf-nombre-fiscal" /></Field>
          </div>
        </>
      )}

      <h4 style={h4}>🏦 Cuenta para depósitos</h4>
      <div style={grid2}>
        <Field label="Titular de la cuenta"><input style={inp} value={f.titular || ""} onChange={set("titular")} data-testid="pf-titular" /></Field>
        <Field label="Banco"><input style={inp} value={f.banco || ""} onChange={set("banco")} placeholder="BBVA, Banorte…" data-testid="pf-banco" /></Field>
        <Field label="Tipo de cuenta">
          <select style={inp} value={f.tipo_cuenta || "clabe"} onChange={set("tipo_cuenta")} data-testid="pf-tipo">
            <option value="clabe">CLABE (18 dígitos) — recomendado</option>
            <option value="tarjeta">Tarjeta de débito (16 dígitos)</option>
          </select>
        </Field>
        <Field label={f.tipo_cuenta === "tarjeta" ? "Número de tarjeta" : "CLABE interbancaria"}>
          <input style={inp} inputMode="numeric" value={f.cuenta || ""} onChange={set("cuenta")}
            placeholder={f.tipo_cuenta === "tarjeta" ? "16 dígitos" : "18 dígitos"} data-testid="pf-cuenta" />
        </Field>
      </div>

      <h4 style={h4}>🧾 Datos fiscales</h4>
      <div style={grid2}>
        <Field label="RFC"><input style={inp} value={f.rfc || ""} onChange={set("rfc")} placeholder="XAXX010101000" data-testid="pf-rfc" /></Field>
        <Field label="Dirección fiscal"><input style={inp} value={f.direccion_fiscal || ""} onChange={set("direccion_fiscal")} data-testid="pf-direccion" /></Field>
      </div>

      <button onClick={save} disabled={saving} data-testid="pf-save"
        style={{ ...btn, background: NAVY, color: "white", marginTop: 8 }}>
        {saving ? "Guardando…" : "Guardar datos"}
      </button>

      <h4 style={h4}>📎 Documentos fiscales</h4>
      <div style={grid2}>
        <DocUpload label="Constancia de Situación Fiscal" tipo="constancia" current={docs.constancia} uploading={uploading} onUpload={upload} />
        <DocUpload label="Alta ante Hacienda (SAT)" tipo="alta" current={docs.alta} uploading={uploading} onUpload={upload} />
      </div>
      <p style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>Formatos: PDF, JPG o PNG (máx 8MB).</p>
    </div>
  );
}

function Field({ label, children }) {
  return (<div style={{ marginBottom: 12 }}><label style={lbl}>{label}</label>{children}</div>);
}

function DocUpload({ label, tipo, current, uploading, onUpload }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={lbl}>{label}</label>
      {current
        ? <div style={{ fontSize: 13, color: GREEN, fontWeight: 700, marginBottom: 6 }} data-testid={`pf-doc-${tipo}-ok`}><i className="fas fa-file-circle-check" /> {current.filename || "Subido"}</div>
        : <div style={{ fontSize: 13, color: "#b45309", marginBottom: 6 }} data-testid={`pf-doc-${tipo}-missing`}>Pendiente</div>}
      <input type="file" accept=".pdf,.jpg,.jpeg,.png" data-testid={`pf-doc-${tipo}-input`}
        disabled={uploading === tipo}
        onChange={(e) => onUpload(tipo, e.target.files?.[0])} style={{ fontSize: 13 }} />
      {uploading === tipo && <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 6 }}>Subiendo…</span>}
    </div>
  );
}

const grid2 = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 14 };
const lbl = { display: "block", fontSize: 12, fontWeight: 700, color: NAVY, marginBottom: 4 };
const inp = { width: "100%", padding: "11px 13px", borderRadius: 10, border: "1px solid #cbd5e1", fontSize: 15, boxSizing: "border-box" };
const btn = { padding: "12px 26px", borderRadius: 999, fontWeight: 800, border: "none", cursor: "pointer", fontSize: 15 };
const h4 = { color: NAVY, marginTop: 22, marginBottom: 10, fontSize: 15 };
const badge = (color, bg) => ({ background: bg, color, border: `1px solid ${color}`, borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 600, marginBottom: 18 });
